import { MerchantApplicationData, BeneficialOwner } from '@/features/merchant-application/types';
import { PAYFAC_INDUSTRY_CODES, TRADITIONAL_INDUSTRY_CODES } from '@/features/merchant-application/types/industryClassifications';

// PayArc API Response interfaces
interface PayArcResponse {
  success: boolean;
  data?: unknown;
  message?: string;
  errors?: string[];
  MerchantCode?: string;
}

interface PayArcLeadPayload {
  Lead: PayArcLead;
  BlendedRatePricing: PayArcBlendedPricing;
  Questionnaire: PayArcQuestionnaire;
  Owners: PayArcOwner[];
  BeneficialOwners?: PayArcOwner[]; // Additional beneficial owners beyond the primary
}

interface PayArcLead {
  // Contact Information
  ContactFirstName: string;
  ContactLastName: string;
  ContactEmail: string;
  ContactPhoneNo: string;
  
  // Business Information
  MerchantName: string;
  LegalName: string;
  TaxId: string;
  BusinessSummary: string;
  Website?: string;
  CustomerSupportNumber?: string;
  
  // Business Structure
  StateOfIncorporation: string;
  DateIncorporated: string;
  FoundationDate: string;
  IrsFilingType: string;
  SicMccCode: number;
  
  // Addresses
  OfficeAddress: string;
  OfficeCity: string;
  OfficeState: string;
  OfficeZipCode: string;
  RegisteredAddress: string;
  RegisteredCity: string;
  RegisteredState: string;
  RegisteredZipCode: string;
  MerchantAccountAddress: string;
  MerchantAccountCity: string;
  MerchantAccountState: string;
  MerchantZipCode: string;
  
  // Processing Information
  TotalMonthlyProcessing: number;
  AvgTicketValue: number;
  HighTicketValue: number;
  CardPresentPct: number;
  MotoPct: number;
  SalesB2BPct: number;
  SalesB2CPct: number;
  SalesKeyedInternetPct: number;
  SalesKeyedMailPct: number;
  SalesKeyedPhonePct: number;
  
  // Banking
  MerchantBankName: string;
  BankRoutingNo: string;
  BankAccountNo: string;
  bank_account_type?: string;
  
  // Risk Assessment
  ProcessingTerminated: string;
  Bankruptcy: string;
  
  // Transaction Details
  DesiredDescriptor: string;
  AmexProcessingPerYear: number;
  
  // Product/Service
  SalesProductServices: string;
  
  // Sales Data
  SalesAnnualMastercard: number;
  SalesTotalAnnual: number;
  SalesDeliveryOffer: string;
  SalesDeliveryDays: string;
  SalesDeliveryDays0Pct: string;
  SalesDeliveryDays1Pct: string;
  SalesDeliveryDays8Pct: string;
  SalesDeliveryDays15Pct: string;
  SalesDeliveryDays30Pct: string;
  SalesTradeshowPct: string;
  SalesCardB2BPct: string;
  SalesCardB2CPct: string;
  
  // Identification
  IdentificationNumberType: string;
  
  
  // Pricing Program
  DiscountRateProgram: string;
  
  // Fees (using database pricing)
  AccountUpdater: number;
  MinMonthlyFee: number;
  MonthlyFee: number;
  PosFee: number;
  PciFee: number;
  TerminalFee: number;
  OnlineReportingFee: number;
  GatewayFee: number;
  StatementFee: number;
  RiskAnalysisFee: number;
  WebMonitoringFee: number;
  BankSponsorFee: number;
  AmexSponsorFee: number;
  BatchFee: number;
  ChargebackFee: number;
  ChargebackReversalFee: number;
  RetrievalFee: number;
  ArbitrationFee: number;
  VoiceAuthFee: number;
  DebitCardAuthsFee: number;
  EbtAuthsFee: number;
  AccountUpdateOtherFee: number;
  GatewayTranactionsFee: number;
  PerAchRejectFee: number;
  DiscoverMAPAuthsFee: number;
  AmexEsaAuthFee: number;
  DeclinesFee: number;
  RefundsFee: number;
  AvsFee: number;
  EthocaVerifiAlertsFee: number;
  RapidDisputeResolutionFee: number;
  MonthlyOtherFee: number;
  MonthlyOtherFeeName: string;
  AmexEsaNumber: string;
  
  // Additional Fields
  CurrentyAcceptCreditCards: string;
  ProcessingType: string;
  Industry: string;
  Country: string;
  OwnershipType: string;  // PayArc expects this exact field name (per line 888 in payarc.md)
  MerchantOwnershipType?: string; // Some validators expect this legacy alias
  same_day_ach?: boolean;          // Required by PayArc API - must be boolean
  MerchantSSN?: string;            // Only for Sole Prop; omit otherwise
  MerchantOtherTaxId?: string;     // Only when IdentificationNumberType === '3'
  owner_position?: string;         // Required by PayArc - primary owner position
  owner1_title?: string;           // Required by PayArc - primary owner title
}

interface PayArcBlendedPricing {
  FlatRatePct: number;
  Authorizations: number;
  CashDiscountRatePct: number;
  CashDiscountFee: number;
  SurchargeFeeRate: number;
  SurchargeFeeAmount: number;
  BlendedBaseCharge: number;
  BlendedPercentageCharge: number;
}

interface PayArcQuestionnaire {
  WebsitePayments: string;
  SSLProvider: string;
  ProdSoldNorthAmericaPct: number;
  ProdSoldEuropePct: number;
  ProdSoldOtherMarketsPct: number;
  SellBusinessPct: number;
  SellConsumerPct: number;
  ReturnPolicy: string;
  NoRefundsInfo: string;
  ReturnPolicyOther: string;
  WhenIsCustomerCharged: string;
  IsShipmentTraceable: string;
  IsDeliveryReceiptRequested: string;
  OrderTurnaroundTime: number;
  AdvancedDeposits: string;
  DepositPct: number;
  IsProcessingSeasonal: string;
  ProcessingSeasonalMonths: string[];
  MembershipPayments: string;
  MembershipPaymentsPct: number;
  PackageUsageTime: number;
  WhoManagesRecurringPayments: string;
  RecurringPaymentProvider: string;
  CaptureCVV: string;
}

interface PayArcOwner {
  FirstName: string;
  LastName: string;
  Title: string;
  OwnershipPct: number;      // PayArc expects this as OwnershipPct (number)
  SSN: string;               // PayArc expects SSN not OwnerSsn
  Address: string;
  Country: string;
  City: string;
  State: string;
  ZipCode: string;
  PhoneNo: string;
  BirthDate: string;
  DriverLicenseNo: string;
  DriverLicenseState: string;
  Email: string;
}

// Utility functions
const ynToBoolean = (val: any, defaultVal = false): boolean => {
  if (val === undefined || val === null) return defaultVal;
  if (typeof val === 'boolean') return val;
  const s = String(val).trim().toLowerCase();
  if (['yes', 'y', 'true', '1'].includes(s)) return true;
  if (['no', 'n', 'false', '0'].includes(s)) return false;
  return defaultVal;
};

const booleanToYesNo = (b: boolean): 'Yes' | 'No' => (b ? 'Yes' : 'No');

const normalizeYNLead = (val: any, defaultVal = false): 'Yes' | 'No' => booleanToYesNo(ynToBoolean(val, defaultVal));

const formatDate = (date: any): string => {
  if (!date) return '';

  const toIsoDateString = (d: Date): string => d.toISOString().split('T')[0];

  if (date instanceof Date) {
    return toIsoDateString(date);
  }

  if (typeof date === 'object' && typeof date.toDate === 'function') {
    try {
      return toIsoDateString(date.toDate());
    } catch (err) {
      console.warn('Unable to convert Firestore Timestamp to date:', err);
    }
  }

  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (!trimmed) return '';

    // Already ISO formatted (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)
    if (/^\d{4}-\d{2}-\d{2}(T.*)?$/.test(trimmed)) {
      return trimmed.split('T')[0];
    }

    // Handle MM/DD/YYYY
    const slashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, mm, dd, yyyy] = slashMatch;
      const month = mm.padStart(2, '0');
      const day = dd.padStart(2, '0');
      return `${yyyy}-${month}-${day}`;
    }

    // Handle YYYY/MM/DD
    const slashYearFirstMatch = trimmed.match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})$/);
    if (slashYearFirstMatch) {
      const [, yyyy, mm, dd] = slashYearFirstMatch;
      const month = mm.padStart(2, '0');
      const day = dd.padStart(2, '0');
      return `${yyyy}-${month}-${day}`;
    }

    // Handle year only
    if (/^\d{4}$/.test(trimmed)) {
      return `${trimmed}-01-01`;
    }

    // Try Date parsing fallback
    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
      return toIsoDateString(parsed);
    }

    console.warn('Unable to parse date string for PayArc payload:', date);
    return '';
  }

  if (typeof date === 'number') {
    const parsed = new Date(date);
    if (!isNaN(parsed.getTime())) {
      return toIsoDateString(parsed);
    }
  }

  console.warn('Unsupported date type encountered for PayArc payload:', date);
  return '';
};

const formatPhone = (phone: string): string => {
  return phone.replace(/\D/g, ''); // Remove all non-digits
};

/**
 * Sanitize text for PayArc "Outlet Product Services Sold" field.
 *
 * PayArc allowed chars (from API error message):
 *   alpha-numeric, dots, spaces, comma, dashes, hyphen, apostrophes,
 *   double quotes, and ^&@#$%*!+;:|=()[]{}\
 *
 * Key pitfalls this handles:
 *  1. Newlines / tabs from textarea input → collapsed to a single space
 *  2. Curly/smart quotes (''""–—) → converted to their ASCII equivalents
 *  3. Any remaining disallowed Unicode stripped
 *  4. Consecutive spaces collapsed, result trimmed
 */
export const sanitizeProductServices = (text: string): string => {
  if (!text) return '';

  let cleaned = text
    // Convert smart/curly quotes and dashes to ASCII equivalents
    .replace(/[\u2018\u2019\u201A\u2039\u203A]/g, "'")   // ' ' ‚ ‹ › → '
    .replace(/[\u201C\u201D\u201E\u00AB\u00BB]/g, '"')   // " " „ « » → "
    .replace(/[\u2013\u2014]/g, '-')                       // – — → -
    .replace(/[\u2026]/g, '...')                            // … → ...
    // Replace all whitespace (newlines, tabs, carriage returns) with a single space
    .replace(/[\r\n\t\f\v]+/g, ' ');

  // Strip any character NOT in PayArc's allowed set (using literal space, not \s)
  // Allowed: a-zA-Z0-9, space, . , - ' " \ ^ & @ # $ % * ! + ; : | = ( ) [ ] { }
  // eslint-disable-next-line no-useless-escape
  cleaned = cleaned.replace(/[^a-zA-Z0-9 \\^&@#$%*!+;:|=()\[\]{}\-",.'/]/g, '');

  // Collapse multiple consecutive spaces into one and trim
  return cleaned.replace(/ {2,}/g, ' ').trim();
};

// Default questionnaire values aligned with Payfac capitalization and semantics
const getDefaultQuestionnaire = (data: MerchantApplicationData): PayArcQuestionnaire => ({
  WebsitePayments: 'no',
  // SSL Provider is a free text field per docs, provide a sane default
  SSLProvider: 'LetsEncrypt',
  ProdSoldNorthAmericaPct: 100,
  ProdSoldEuropePct: 0,
  ProdSoldOtherMarketsPct: 0,
  SellBusinessPct: data.processingHistory?.b2bPercentage || 50,
  SellConsumerPct: data.processingHistory?.b2cPercentage || 50,
  // Use lower-case tokens per API validation feedback
  ReturnPolicy: 'less_than_30_days',
  NoRefundsInfo: '',
  ReturnPolicyOther: '',
  WhenIsCustomerCharged: 'order',
  IsShipmentTraceable: 'yes',
  IsDeliveryReceiptRequested: 'no',
  OrderTurnaroundTime: 7,
  AdvancedDeposits: 'no',
  DepositPct: 0,
  IsProcessingSeasonal: 'no',
  ProcessingSeasonalMonths: [],
  MembershipPayments: 'no',
  MembershipPaymentsPct: 0,
  PackageUsageTime: 30,
  WhoManagesRecurringPayments: 'other',
  RecurringPaymentProvider: '',
  CaptureCVV: 'yes',
});

// Main PayArc Service Class
export class PayArcService {
  private static instance: PayArcService;
  
  static getInstance(): PayArcService {
    if (!PayArcService.instance) {
      PayArcService.instance = new PayArcService();
    }
    return PayArcService.instance;
  }

  private async apiCall(_method: string, endpoint: string, data?: unknown, source?: string, bankingPartnerId?: string): Promise<PayArcResponse> {
    try {
      // Prefer Next.js API route; gracefully fall back to Express proxy if unavailable
      const body = JSON.stringify({ endpoint, data, source, bankingPartnerId });
      console.log('🔧 apiCall body being sent:', { endpoint, source, bankingPartnerId: bankingPartnerId || 'undefined' });
      const headers = { 'Content-Type': 'application/json' } as const;

      let response: Response | null = null;
      let result: any = null;

      try {
        response = await fetch(`/api/payarc-direct`, {
          method: 'POST',
          headers,
          body,
        });

        const contentType = response.headers.get('content-type') || '';
        const missingRoute = response.status === 404;
        const notJson = !contentType.toLowerCase().includes('application/json');

        if (missingRoute || notJson) {
          if (notJson && response.ok) {
            console.warn('PayArc direct route responded with non-JSON content; falling back to Express proxy');
          }
          // Next route likely not mounted in this environment; fall back below
          response = null;
        }
      } catch (_e) {
        // First attempt failed, will fallback to Express proxy
      }

      if (!response) {
        // Fallback to Express proxy
        response = await fetch(`/api/payarc`, {
          method: 'POST',
          headers,
          body,
        });
      }

      try {
        result = await response.json();
      } catch (e) {
        // Ensure we always have a result object
        result = { success: false, message: 'Invalid JSON from server', errors: [(e as any)?.message || 'parse error'] };
      }
      
      if (!result.success) {
        console.error('PayArc API Error:', result);
        return {
          success: false,
          message: result.message || 'API request failed',
          errors: result.errors || [],
        };
      }

      return {
        success: true,
        data: result.data,
        MerchantCode: result.MerchantCode,
      };
    } catch (error) {
      console.error('PayArc API Network Error:', error);
      return {
        success: false,
        message: 'Network error occurred',
        errors: [error.message],
      };
    }
  }

  async createLead(applicationData: MerchantApplicationData, bankingPartnerId?: string): Promise<PayArcResponse> {
    console.log('📞 createLead called with bankingPartnerId:', bankingPartnerId);

    const payload = await this.transformApplicationData(applicationData);
    console.log('Sending PayArc payload:', JSON.stringify(payload, null, 2));
    console.log('Primary Owners count:', payload.Owners.length);
    console.log('Beneficial Owners count:', payload.BeneficialOwners?.length || 0);

    // Get application source for API key resolution
    const source = applicationData.applicationSource;
    if (source) {
      console.log('Using application source for API key:', source);
    } else {
      console.warn('No application source provided, will use default API key');
    }

    if (bankingPartnerId) {
      console.log('✅ Using banking partner ID for API key:', bankingPartnerId);
    } else {
      console.warn('⚠️ No banking partner ID provided to createLead');
    }

    // PayArc expects the data in the exact structure with Lead, BlendedRatePricing, Questionnaire, Owners, and optionally BeneficialOwners
    return this.apiCall('POST', '/add-lead', payload, source, bankingPartnerId);
  }

  async fetchTermsAndConditions(merchantCode: string): Promise<Blob> {
    const endpoints = [
      `/api/payarc/applications/${merchantCode}/terms-and-conditions`,
      `/api/payarc/terms-and-conditions/${merchantCode}`,
    ];

    let lastError: Error | null = null;

    for (const endpoint of endpoints) {
      try {
        const response = await fetch(endpoint, {
          method: 'GET',
          credentials: 'include',
        });

        const contentType = response.headers.get('content-type') || '';

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          lastError = new Error(text || `Failed to fetch terms (${response.status})`);
          continue;
        }

        const normalizedContentType = contentType.toLowerCase();
        const isPdfLike =
          normalizedContentType.includes('application/pdf') ||
          normalizedContentType.includes('application/octet-stream') ||
          normalizedContentType === '';

        if (!isPdfLike) {
          console.warn('PayArc terms returned unexpected content type:', contentType, 'from', endpoint);
        }

        return await response.blob();
      } catch (error: any) {
        lastError = error instanceof Error ? error : new Error(String(error));
      }
    }

    throw lastError || new Error('Unable to fetch PayArc terms and conditions');
  }

  async submitApplicationForUnderwriting(merchantCode: string, bankingPartnerId?: string): Promise<PayArcResponse> {
    const submissionPayload: Record<string, unknown> = {
      merchant_code: merchantCode,
      MerchantCode: merchantCode,
      bankingPartnerId, // Pass banking partner ID to the submit route
    };
    let leadStatusSnapshot: unknown;

    try {
      const status = await this.apiCall('POST', '/lead-status', { MerchantCode: merchantCode }, undefined, bankingPartnerId);

      console.log('[PayArc] lead-status response:', status);

      if (status.success) {
        const rawStatus = status.data as Record<string, any> | undefined;
        leadStatusSnapshot = rawStatus;

        if (rawStatus && typeof rawStatus === 'object') {
          const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');
          const findNestedValue = (root: unknown, candidateKeys: string[]): unknown => {
            const targetKeys = candidateKeys.map(normalizeKey);
            const stack: unknown[] = [root];
            const visited = new Set<unknown>();

            while (stack.length > 0) {
              const current = stack.pop();
              if (!current || typeof current !== 'object') continue;
              if (visited.has(current)) continue;
              visited.add(current);

              if (Array.isArray(current)) {
                for (const item of current) {
                  if (item && typeof item === 'object') {
                    stack.push(item);
                  }
                }
                continue;
              }

              for (const [key, value] of Object.entries(current as Record<string, unknown>)) {
                const normalized = normalizeKey(key);
                if (targetKeys.includes(normalized) && (typeof value === 'string' || typeof value === 'number')) {
                  return value;
                }

                if (value && typeof value === 'object') {
                  stack.push(value);
                }
              }
            }

            return undefined;
          };

          const applyAppIdCandidate = findNestedValue(rawStatus, [
            'apply_app_id',
            'applyappid',
            'applyid',
            'applicationid',
            'applyapplicationid',
          ]);

          const leadCodeCandidate = findNestedValue(rawStatus, [
            'leadcode',
            'lead_id',
            'leadid',
            'leadidentifier',
          ]);

          const applyAppId =
            applyAppIdCandidate ||
            rawStatus.Apply_App_Id ||
            rawStatus.apply_app_id ||
            rawStatus.ApplyAppId ||
            rawStatus.applyAppId ||
            rawStatus?.Lead?.Apply_App_Id ||
            rawStatus?.Lead?.apply_app_id ||
            rawStatus.LeadCode ||
            rawStatus.leadCode ||
            rawStatus?.Lead?.LeadCode;

          if (applyAppId !== undefined && applyAppId !== null && applyAppId !== '') {
            const normalizedApplyAppId = String(applyAppId);
            submissionPayload.apply_app_id = normalizedApplyAppId;
            submissionPayload.applyAppId = normalizedApplyAppId;
          }

          const leadCode =
            leadCodeCandidate ||
            rawStatus.LeadCode ||
            rawStatus.leadCode ||
            rawStatus?.Lead?.LeadCode ||
            rawStatus?.lead?.leadCode;

          if (leadCode !== undefined && leadCode !== null && leadCode !== '') {
            const normalizedLeadCode = String(leadCode);
            submissionPayload.lead_code = normalizedLeadCode;
            submissionPayload.LeadCode = normalizedLeadCode;
          }
        }
      } else {
        console.warn('PayArc lead-status request failed before underwriting submission:', status);
      }
    } catch (statusError) {
      console.warn('Unable to retrieve PayArc lead status prior to underwriting submission:', statusError);
    }

    const attempt = async (url: string) => {
      console.log('[PayArc] underwriting payload:', submissionPayload);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submissionPayload),
      });

      const result = await response
        .json()
        .catch(() => ({ success: false, message: 'Invalid JSON response from server' }));

      return { response, result } as const;
    };

    try {
      let { response, result } = await attempt(`/api/payarc/applications/${merchantCode}/submit`);

      if (!response.ok && response.status === 404) {
        const base =
          (typeof window !== 'undefined' && window.location?.origin) ||
          import.meta.env.VITE_NEXTJS_API_URL ||
          import.meta.env.VITE_API_URL ||
          '';

        if (base) {
          const normalizedBase = base.replace(/\/$/, '');
          ({ response, result } = await attempt(`${normalizedBase}/api/payarc/applications/${merchantCode}/submit`));
        }
      }

      if (!response.ok || !result?.success) {
        const missingApplyAppId = !submissionPayload.apply_app_id;
        const fallbackMessage = missingApplyAppId
          ? 'Failed to submit PayArc application (missing apply_app_id from PayArc lead status)'
          : 'Failed to submit PayArc application';

        return {
          success: false,
          message: result?.message || fallbackMessage,
          errors: result?.errors || [],
          data: { ...result?.details, leadStatusSnapshot, missingApplyAppId },
        };
      }

      return {
        success: true,
        data: result.data,
      };
    } catch (error: any) {
      console.error('PayArc submit application error:', error);
      return {
        success: false,
        message: 'Network error submitting PayArc application',
        errors: [error?.message || 'Unknown error'],
        data: { leadStatusSnapshot },
      };
    }
  }

  // Send a raw payload directly (used by troubleshooting editor)
  async sendRawLeadPayload(payload: unknown, bankingPartnerId?: string): Promise<PayArcResponse> {
    try {
      console.log('Sending RAW PayArc payload (manual override):', JSON.stringify(payload, null, 2));
      console.log('Using bankingPartnerId:', bankingPartnerId || 'NOT PROVIDED');
    } catch {}
    return this.apiCall('POST', '/add-lead', payload, undefined, bankingPartnerId);
  }

  // Generate payload for preview without sending
  async generatePayload(applicationData: MerchantApplicationData): Promise<{ payload: PayArcLeadPayload; errors: string[] }> {
    console.log('Application data received:', JSON.stringify(applicationData, null, 2));
    const payload = await this.transformApplicationData(applicationData);
    const errors = this.validatePayload(payload);
    return { payload, errors };
  }

  // Validate payload and return list of errors
  private validatePayload(payload: PayArcLeadPayload): string[] {
    const errors: string[] = [];
    const lead = payload.Lead;

    // Check required fields
    if (!lead.ContactFirstName) errors.push('Missing Contact First Name');
    if (!lead.ContactLastName) errors.push('Missing Contact Last Name');
    if (!lead.ContactEmail) errors.push('Missing Contact Email');
    if (!lead.ContactPhoneNo) errors.push('Missing Contact Phone Number');
    
    if (!lead.MerchantName) errors.push('Missing Merchant Name');
    if (!lead.LegalName) errors.push('Missing Legal Name');
    if (!lead.TaxId) errors.push('Missing Tax ID/EIN');
    if (!lead.BusinessSummary) errors.push('Missing Business Summary');
    
    // Only show warning if SIC code is truly missing (not if it's 8099, which is a valid code)
    if (!lead.SicMccCode) {
      errors.push('SIC/MCC Code not specified');
    }
    
    if (!lead.OfficeAddress) errors.push('Missing Business Address');
    if (!lead.OfficeCity) errors.push('Missing Business City');
    if (!lead.OfficeState) errors.push('Missing Business State');
    if (!lead.OfficeZipCode) errors.push('Missing Business Zip Code');
    
    if (!lead.MerchantBankName) errors.push('Missing Bank Name');
    if (!lead.BankRoutingNo) errors.push('Missing Bank Routing Number');
    if (!lead.BankAccountNo) errors.push('Missing Bank Account Number');
    
    if (lead.TotalMonthlyProcessing <= 0) errors.push('Monthly Processing Volume must be greater than 0');
    if (lead.AvgTicketValue <= 0) errors.push('Average Ticket Value must be greater than 0');
    
    // Check processing percentages
    const totalProcessing = lead.CardPresentPct + lead.MotoPct;
    if (Math.abs(totalProcessing - 100) > 0.01) {
      errors.push(`Processing percentages must total 100% (currently ${totalProcessing}%)`);
    }
    
    const totalB2B = lead.SalesB2BPct + lead.SalesB2CPct;
    if (Math.abs(totalB2B - 100) > 0.01) {
      errors.push(`B2B/B2C percentages must total 100% (currently ${totalB2B}%)`);
    }
    
    // Check primary owner
    if (!payload.Owners || payload.Owners.length === 0) {
      errors.push('At least one primary owner is required');
    } else {
      let totalOwnership = 0;
      
      // Validate primary owner
      payload.Owners.forEach((owner) => {
        if (!owner.FirstName) errors.push(`Primary Owner: Missing First Name`);
        if (!owner.LastName) errors.push(`Primary Owner: Missing Last Name`);
        if (!owner.SSN) errors.push(`Primary Owner: Missing SSN`);
        totalOwnership += owner.OwnershipPct || 0;
      });
      
      // Validate beneficial owners if present
      if (payload.BeneficialOwners) {
        payload.BeneficialOwners.forEach((owner, index) => {
          if (!owner.FirstName) errors.push(`Beneficial Owner ${index + 1}: Missing First Name`);
          if (!owner.LastName) errors.push(`Beneficial Owner ${index + 1}: Missing Last Name`);
          if (!owner.SSN) errors.push(`Beneficial Owner ${index + 1}: Missing SSN`);
          totalOwnership += owner.OwnershipPct || 0;
        });
      }
      
      if (Math.abs(totalOwnership - 100) > 0.01) {
        errors.push(`Ownership percentages must total 100% (currently ${totalOwnership}%)`);
      }
    }

    return errors;
  }

  async uploadDocuments(leadId: string, documents: unknown[]): Promise<PayArcResponse> {
    // TODO: Implement document upload
    // Will need to handle file uploads to PayArc
    console.log('Uploading documents for lead:', leadId, 'Document count:', documents.length);
    return { success: true, message: 'Document upload not yet implemented' };
  }

  async submitForSignature(leadId: string): Promise<PayArcResponse> {
    return this.apiCall('POST', '/submit-for-signature', { LeadId: leadId });
  }

  async getLeadStatus(merchantCode: string): Promise<PayArcResponse> {
    return this.apiCall('POST', '/lead-status', { MerchantCode: merchantCode });
  }

  private async transformApplicationData(data: MerchantApplicationData): Promise<PayArcLeadPayload> {
    // Transform our application data to PayArc format
    const lead = await this.buildLeadData(data);
    const pricing = await this.buildPricingData(data);
    const questionnaire = getDefaultQuestionnaire(data);
    const processingHistory = data.processingHistory || {};

    // Normalize fulfillment-related questionnaire fields from tenant configuration/application data
    const normalizeChargeTiming = (value: unknown): 'order' | 'delivery' | 'shipment' => {
      const allowed = ['order', 'delivery', 'shipment'] as const;
      if (typeof value === 'string') {
        const lower = value.toLowerCase();
        if (allowed.includes(lower as typeof allowed[number])) {
          return lower as typeof allowed[number];
        }
      }
      return 'order';
    };

    const normalizedChargeTiming = normalizeChargeTiming(
      (processingHistory as any).whenIsCustomerCharged
    );
    questionnaire.WhenIsCustomerCharged = normalizedChargeTiming;

    const normalizeNonNegativeNumber = (value: unknown, fallback: number, max?: number) => {
      const numeric = Number(value);
      if (!Number.isFinite(numeric) || numeric < 0) return fallback;
      if (typeof max === 'number') {
        return Math.min(numeric, max);
      }
      return numeric;
    };

    questionnaire.OrderTurnaroundTime = normalizeNonNegativeNumber(
      (processingHistory as any).orderTurnaroundTime,
      questionnaire.OrderTurnaroundTime
    );

    const advancedDeposits = (processingHistory as any).advancedDeposits === 'yes' ? 'yes' : 'no';
    questionnaire.AdvancedDeposits = advancedDeposits;

    const depositPct = normalizeNonNegativeNumber(
      (processingHistory as any).depositPct,
      questionnaire.DepositPct,
      100
    );
    questionnaire.DepositPct = advancedDeposits === 'yes' ? depositPct : 0;

    // Separate primary owner from additional beneficial owners
    const allOwners = this.buildOwnersData(data.beneficialOwners || []);
    
    // First owner (who started the application) goes in "Owners" array
    const primaryOwners = allOwners.length > 0 ? [allOwners[0]] : [];
    
    // Additional owners go in "BeneficialOwners" array
    const beneficialOwners = allOwners.length > 1 ? allOwners.slice(1) : undefined;

    const payload: PayArcLeadPayload = {
      Lead: lead,
      BlendedRatePricing: pricing,
      Questionnaire: questionnaire,
      Owners: primaryOwners,
    };
    
    // Only include BeneficialOwners if there are additional owners
    if (beneficialOwners && beneficialOwners.length > 0) {
      payload.BeneficialOwners = beneficialOwners;
    }
    
    return payload;
  }

  private async buildLeadData(data: MerchantApplicationData): Promise<PayArcLead> {
    // Debug logging for important fields
    console.log('Full application data:', JSON.stringify(data, null, 2));
    console.log('Corporation Info:', data.corporationInfo);
    console.log('SIC Code from data.corporationInfo:', data.corporationInfo?.sicCode);
    console.log('Direct SIC Code from data:', (data as any).sicCode);
    console.log('Corporation Type from data:', data.corporationInfo?.corporationType);
    console.log('Application Type:', data.applicationType);
    
    // Debug logging for bank details
    console.log('Bank details from data.bankDetails:', data.bankDetails);
    console.log('Direct bank fields - bankName:', (data as any).bankName, 'routingNumber:', (data as any).routingNumber, 'accountNumber:', (data as any).accountNumber);
    
    // Get bank details - check both nested and direct properties
    const bankName = data.bankDetails?.bankName || (data as any).bankName || '';
    const routingNumber = data.bankDetails?.routingNumber || (data as any).routingNumber || '';
    const accountNumber = data.bankDetails?.accountNumber || (data as any).accountNumber || '';
    const accountTypeRaw = (data.bankDetails?.accountType || (data as any).accountType || 'checking') as string;
    const accountTypeNormalized = accountTypeRaw?.toLowerCase() === 'savings' ? 'savings' : 'checking';
    const bankAccountTypeCode = accountTypeNormalized === 'savings' ? '02' : '01';

    console.log('Resolved bank details - Name:', bankName, 'Routing:', routingNumber, 'Account:', accountNumber);
    console.log('Resolved bank account type:', accountTypeNormalized, 'Mapped code:', bankAccountTypeCode);

    const salesDeliveryDaysRaw =
      (data.processingHistory as any)?.salesDeliveryDays ??
      (data as any).salesDeliveryDays ??
      0;
    const normalizedSalesDeliveryDays = (() => {
      const numeric = Number(salesDeliveryDaysRaw);
      if (!Number.isFinite(numeric)) return 0;
      return Math.max(0, Math.round(numeric));
    })();
    
    // Get business address and ensure all fields are populated
    const businessAddress = data.corporationInfo?.businessAddress;
    
    // Use business address for all PayArc address requirements
    const address = this.normalizeAddress(businessAddress);

    // Determine MotoPct from CRM online/ecommerce percentage; CardPresentPct is the remainder
    const ecommercePctRaw = Number(
      (data.processingHistory as any)?.ecommercePercentage ??
      (data.processingHistory as any)?.onlinePercentage ??
      (100 - Number((data.processingHistory as any)?.cardPresentPercentage ?? 0))
    );
    const motoPct = isNaN(ecommercePctRaw) ? 0 : Math.max(0, Math.min(100, ecommercePctRaw));
    const cardPresentPct = Math.max(0, Math.min(100, 100 - motoPct));
    
    const b2bPct = data.processingHistory?.b2bPercentage || 50;
    const b2cPct = data.processingHistory?.b2cPercentage || 50;

    const businessStartDateInput =
      data.corporationInfo?.businessStartDate ||
      (data as any).corporationInfo?.yearEstablished ||
      (data as any).businessStartDate ||
      (data as any).yearEstablished;

    const businessStartDateIso = formatDate(businessStartDateInput);

    // Fetch pricing template if selected to get all fee values and risk type
    let templateFees: any = null;
    let templateRiskType: 'highRisk' | 'lowRisk' | null = null;
    if (data.pricingTemplateId && data.selectedBankingPartnerId) {
      try {
        console.log('Fetching pricing template fees for Lead:', data.pricingTemplateId);
        const { bankingPartnerService } = await import('@/features/banking-partners/services/bankingPartnerService');
        const template = await bankingPartnerService.getPricingTemplate(
          data.selectedBankingPartnerId,
          data.pricingTemplateId
        );
        if (template) {
          console.log('Using template fees:', template.pricing);
          console.log('Template risk type:', template.riskType);
          templateFees = template.pricing;
          templateRiskType = template.riskType;
        }
      } catch (error) {
        console.error('Error fetching template for Lead fees:', error);
      }
    }

    // Access pricing as any to allow dynamic field access for extended fields
    const pricingData = data.pricing as unknown as Record<string, unknown> | undefined;

    // Resolve SIC/MCC code early — its classification drives ProcessingType and Industry format
    const resolvedSic = this.parseSicCode(
      data.corporationInfo?.sicCode ||
      (data as any).sicCode ||
      (data.corporationInfo as any)?.SicMccCode ||
      (data as any).SicMccCode
    );
    const { processingType, industryValue } = this.resolveIndustryForPayArc(resolvedSic);
    const isPayFac = processingType === 'Payfac';

    // Log when SIC classification overrides the pricing template's risk type
    if (templateRiskType === 'highRisk' && isPayFac) {
      console.warn(
        `[PayArc] SIC code ${resolvedSic} is classified as Payfac but pricing template is highRisk. ` +
        `Using Payfac processing type based on SIC code classification.`
      );
    } else if (templateRiskType === 'lowRisk' && !isPayFac) {
      console.warn(
        `[PayArc] SIC code ${resolvedSic} is classified as Traditional but pricing template is lowRisk. ` +
        `Using Traditional processing type based on SIC code classification.`
      );
    }

    // Helper to get fee value based on risk type:
    // - PayFac (lowRisk): Use hardcoded default, but CRM can override
    // - Traditional (highRisk): Use CRM value only (no hardcoded defaults)
    const getFeeValue = (
      crmValue: unknown,
      templateValue: unknown,
      payfacDefault: number
    ): number => {
      // CRM value always takes priority if set
      const crmParsed = Number(crmValue);
      if (Number.isFinite(crmParsed) && crmParsed >= 0) {
        return crmParsed;
      }

      // For Traditional (highRisk): use template value or 0 (no hardcoded defaults)
      if (!isPayFac) {
        const templateParsed = Number(templateValue);
        return Number.isFinite(templateParsed) && templateParsed >= 0 ? templateParsed : 0;
      }

      // For PayFac (lowRisk): use hardcoded default (CRM would have overridden above if set)
      return payfacDefault;
    };

    console.log('Risk type for fees:', isPayFac ? 'PayFac (lowRisk)' : 'Traditional (highRisk)');

    // Get fee values based on risk type
    const chargebackFee = getFeeValue(pricingData?.chargebackFee, templateFees?.chargebackFee, 0);
    const monthlyMinimumFee = getFeeValue(pricingData?.monthlyMinimum, templateFees?.monthlyMinimumFee, 0);
    const monthlyFee = getFeeValue(pricingData?.monthlyFee, templateFees?.monthlyFee, 0);
    const pciFee = getFeeValue(pricingData?.pciFee, templateFees?.pciFee, 0);
    const retrievalFee = getFeeValue(pricingData?.retrievalFee, templateFees?.retrievalFee, 0);
    const avsFee = getFeeValue(pricingData?.avsFee, templateFees?.avsFee, 0);
    const sponsorFee = getFeeValue(pricingData?.sponsorFee, templateFees?.sponsorFee, 0);

    // Amex fees
    const amexProcessingFeePct = getFeeValue(
      pricingData?.amexProcessingFee,
      templateFees?.processingFees?.amex,
      3.3 // PayFac default
    );
    const amexTransactionFee = getFeeValue(
      pricingData?.amexTransactionFee,
      templateFees?.transactionFees?.amex,
      0.3 // PayFac default
    );

    // All other fees - PayFac uses hardcoded 1.0, Traditional uses CRM or 0
    const accountUpdaterFee = getFeeValue(pricingData?.accountUpdaterFee, templateFees?.accountUpdaterFee, 1.0);
    const posFee = getFeeValue(pricingData?.posFee, templateFees?.posFee, 1.0);
    const terminalFee = getFeeValue(pricingData?.terminalFee, templateFees?.terminalFee, 1.0);
    const onlineReportingFee = getFeeValue(pricingData?.onlineReportingFee, templateFees?.onlineReportingFee, 1.0);
    const gatewayFee = getFeeValue(pricingData?.gatewayFee, templateFees?.gatewayFee, 1.0);
    const statementFee = getFeeValue(pricingData?.statementFee, templateFees?.statementFee, 1.0);
    const riskAnalysisFee = getFeeValue(pricingData?.riskAnalysisFee, templateFees?.riskAnalysisFee, 1.0);
    const webMonitoringFee = getFeeValue(pricingData?.webMonitoringFee, templateFees?.webMonitoringFee, 1.0);
    const batchFee = getFeeValue(pricingData?.batchFee, templateFees?.batchFee, 1.0);
    const chargebackReversalFee = getFeeValue(pricingData?.chargebackReversalFee, templateFees?.chargebackReversalFee, 1.0);
    const arbitrationFee = getFeeValue(pricingData?.arbitrationFee, templateFees?.arbitrationFee, 1.0);
    const voiceAuthFee = getFeeValue(pricingData?.voiceAuthFee, templateFees?.voiceAuthFee, 1.0);
    const debitCardAuthsFee = getFeeValue(pricingData?.debitCardAuthsFee, templateFees?.debitCardAuthsFee, 1.0);
    const ebtAuthsFee = getFeeValue(pricingData?.ebtAuthsFee, templateFees?.ebtAuthsFee, 1.0);
    const accountUpdateOtherFee = getFeeValue(pricingData?.accountUpdateOtherFee, templateFees?.accountUpdateOtherFee, 1.0);
    const gatewayTransactionsFee = getFeeValue(pricingData?.gatewayTransactionsFee, templateFees?.gatewayTransactionsFee, 1.0);
    const perAchRejectFee = getFeeValue(pricingData?.perAchRejectFee, templateFees?.perAchRejectFee, 1.0);
    const discoverMapAuthsFee = getFeeValue(pricingData?.discoverMapAuthsFee, templateFees?.discoverMapAuthsFee, 1.0);
    const declinesFee = getFeeValue(pricingData?.declinesFee, templateFees?.declinesFee, 1.0);
    const refundsFee = getFeeValue(pricingData?.refundsFee, templateFees?.refundsFee, 1.0);
    const ethocaVerifiAlertsFee = getFeeValue(pricingData?.ethocaVerifiAlertsFee, templateFees?.ethocaVerifiAlertsFee, 1.0);
    const rapidDisputeResolutionFee = getFeeValue(pricingData?.rapidDisputeResolutionFee, templateFees?.rapidDisputeResolutionFee, 1.0);
    const monthlyOtherFee = getFeeValue(pricingData?.monthlyOtherFee, templateFees?.monthlyOtherFee, 3.05);

    console.log('Lead fees calculated:', {
      isPayFac,
      chargebackFee,
      monthlyMinimumFee,
      amexProcessingFeePct,
      amexTransactionFee,
      accountUpdaterFee,
      posFee
    });

    // Determine OwnershipType and identification strategy
    const corpTypeInput = data.corporationInfo?.corporationType || (data.corporationInfo as any)?.businessType;
    const ownershipType = this.mapCorporationType(data.applicationType, corpTypeInput);
    const ownershipCodeMap: Record<string, string> = {
      'LLC': 'L',
      'Government': 'G',
      'Partnership': 'N',
      'Association': 'S',
      'Estate': 'S',
      'Trust': 'S',
      'Non Profit': 'X',
      'Private Corp': 'V',
      'Public Corp': 'U',
      'Sole Prop': 'O',
    };
    const ownershipCode = ownershipCodeMap[ownershipType] || 'V';
    const isSoleProp = ownershipType === 'Sole Prop' || data.applicationType === 'individual';

    // Determine identification number type and values
    // For Sole Prop: Use SSN via MerchantSSN field, NOT TaxId
    // For Business: Use EIN via TaxId field, NOT MerchantSSN
    const identificationNumberType = isSoleProp ? '1' : '2'; // 1 = SSN, 2 = EIN
    const primarySignerSsn = this.formatSSN(data.beneficialOwners?.[0]?.ssn);
    const ein = this.formatTaxId(data.corporationInfo?.ein);
    // taxIdValue is ONLY for businesses (EIN) - Sole Props use MerchantSSN instead
    const taxIdValue = isSoleProp ? '' : ein;

    const businessStartDateForPayload = businessStartDateIso || formatDate(new Date());

    if (!businessStartDateIso) {
      console.warn('PayArc lead missing explicit business start date; using fallback date', businessStartDateForPayload);
    }

    const lead: PayArcLead = {
      // Contact Information (direct mapping)
      ContactFirstName: (data.beneficialOwners?.[0]?.firstName) || data.personalInfo?.firstName || '',
      ContactLastName: (data.beneficialOwners?.[0]?.lastName) || data.personalInfo?.lastName || '',
      ContactEmail: (data.beneficialOwners?.[0]?.email) || data.personalInfo?.email || '',
      ContactPhoneNo: formatPhone((data.beneficialOwners?.[0]?.phone) || data.personalInfo?.phone || ''),
      
      // Business Information - MerchantName limited to 25 chars, LegalName to 24 chars per PayArc requirements
      // Both must contain only letters, numbers, and spaces
      MerchantName: sanitizeProductServices(data.corporationInfo?.dba || data.corporationInfo?.businessName || '').slice(0, 25),
      LegalName: sanitizeProductServices(data.corporationInfo?.businessName || '').slice(0, 24),
      TaxId: taxIdValue,
      BusinessSummary: sanitizeProductServices(data.corporationInfo?.businessDescription || ''),
      Website: data.corporationInfo?.website || '',
      CustomerSupportNumber: formatPhone(data.personalInfo?.phone || ''),
      
      // Business Structure (computed fields) 
      StateOfIncorporation: address.state,
      DateIncorporated: businessStartDateForPayload,
      FoundationDate: businessStartDateForPayload,
      IrsFilingType: this.getIrsFilingType(data),
      // SIC/MCC
      SicMccCode: resolvedSic,
      
      // Addresses (use business address for all)
      OfficeAddress: address.street,
      OfficeCity: address.city,
      OfficeState: address.state,
      OfficeZipCode: address.zipCode,
      RegisteredAddress: address.street,
      RegisteredCity: address.city,
      RegisteredState: address.state,
      RegisteredZipCode: address.zipCode,
      MerchantAccountAddress: address.street,
      MerchantAccountCity: address.city,
      MerchantAccountState: address.state,
      MerchantZipCode: address.zipCode,
      
      // Processing Information
      TotalMonthlyProcessing: data.processingHistory?.monthlyVolume || 0,
      AvgTicketValue: data.processingHistory?.averageTicket || 0,
      HighTicketValue: data.processingHistory?.highTicket || 0,
      CardPresentPct: cardPresentPct,
      MotoPct: motoPct,
      SalesB2BPct: b2bPct,
      SalesB2CPct: b2cPct,
      // Keyed channels: all ecommerce to Internet; Mail and Phone are 0
      SalesKeyedInternetPct: motoPct,
      SalesKeyedMailPct: 0,
      SalesKeyedPhonePct: 0,
      
      // Banking (use resolved values from above)
      MerchantBankName: bankName,
      BankRoutingNo: routingNumber,
      BankAccountNo: accountNumber,
      bank_account_type: bankAccountTypeCode,
      
      // Risk Assessment (Payfac expects capitalized Yes/No)
      ProcessingTerminated: normalizeYNLead((data.processingHistory as any)?.hasBeenTerminated, false),
      Bankruptcy: normalizeYNLead((data.processingHistory as any)?.bankruptcy, false),
      
      // Transaction Details - Use DBA for statement descriptor, fallback to business name
      DesiredDescriptor: sanitizeProductServices(data.corporationInfo?.dba || data.corporationInfo?.businessName || ''),
      AmexProcessingPerYear: Math.round((data.processingHistory?.monthlyVolume || 0) * 12 * 0.1),
      
      // Product/Service - sanitize to only allow PayArc-accepted characters
      SalesProductServices: sanitizeProductServices(data.corporationInfo?.businessDescription || ''),
      
      // Pricing Program
      DiscountRateProgram: 'bundleRate',

      // Fees - PayFac uses hardcoded defaults (with CRM override), Traditional uses CRM values
      AccountUpdater: accountUpdaterFee,
      MinMonthlyFee: monthlyMinimumFee,
      MonthlyFee: monthlyFee,
      PosFee: posFee,
      PciFee: pciFee,
      TerminalFee: terminalFee,
      OnlineReportingFee: onlineReportingFee,
      GatewayFee: gatewayFee,
      StatementFee: statementFee,
      RiskAnalysisFee: riskAnalysisFee,
      WebMonitoringFee: webMonitoringFee,
      BankSponsorFee: sponsorFee,
      AmexSponsorFee: amexProcessingFeePct,
      BatchFee: batchFee,
      ChargebackFee: chargebackFee,
      ChargebackReversalFee: chargebackReversalFee,
      RetrievalFee: retrievalFee,
      ArbitrationFee: arbitrationFee,
      VoiceAuthFee: voiceAuthFee,
      DebitCardAuthsFee: debitCardAuthsFee,
      EbtAuthsFee: ebtAuthsFee,
      AccountUpdateOtherFee: accountUpdateOtherFee,
      GatewayTranactionsFee: gatewayTransactionsFee,
      PerAchRejectFee: perAchRejectFee,
      DiscoverMAPAuthsFee: discoverMapAuthsFee,
      AmexEsaAuthFee: amexTransactionFee,
      DeclinesFee: declinesFee,
      RefundsFee: refundsFee,
      AvsFee: avsFee,
      EthocaVerifiAlertsFee: ethocaVerifiAlertsFee,
      RapidDisputeResolutionFee: rapidDisputeResolutionFee,
      MonthlyOtherFee: monthlyOtherFee,
      MonthlyOtherFeeName: 'Other Fee',
      AmexEsaNumber: '',
      
      // Sales Data (Required by PayArc) - Hard coded values per request
      SalesAnnualMastercard: Math.round((data.processingHistory?.monthlyVolume || 0) * 12 * 0.4), // Monthly * 12 * 40% as per PayArc requirements
      SalesTotalAnnual: Math.round((data.processingHistory?.monthlyVolume || 0) * 12),
      // Delivery settings: keep tokens consistent with questionnaire (lowercase)
      // and align SalesDeliveryDays with 0-day 100% split
      SalesDeliveryOffer: 'yes',
      SalesDeliveryDays: String(normalizedSalesDeliveryDays),
      SalesDeliveryDays0Pct: '100',  // Hard coded to 100%
      SalesDeliveryDays1Pct: '0',    // Hard coded to 0%
      SalesDeliveryDays8Pct: '0',
      SalesDeliveryDays15Pct: '0',
      SalesDeliveryDays30Pct: '0',
      SalesTradeshowPct: '0',
      SalesCardB2BPct: b2bPct.toString(),
      SalesCardB2CPct: b2cPct.toString(),
      
      // Identification Number Type (2 = EIN)
      IdentificationNumberType: identificationNumberType,
      
      // Additional required fields
      CurrentyAcceptCreditCards: normalizeYNLead((data.processingHistory as any)?.isCurrentlyProcessing, false),
      ProcessingType: processingType, // Mapped from pricing template riskType: lowRisk → Payfac, highRisk → Traditional
      Industry: industryValue, // PayArc expects a valid MCC code value as a string
      Country: 'US',
      OwnershipType: ownershipCode,
      // Omit MerchantOwnershipType to avoid validator mismatch
      same_day_ach: false, // Required field - must be boolean
    } as PayArcLead;

    // Add owner position/title for ALL applications (required by PayArc API - undocumented but mandatory)
    (lead as any).owner_position = this.normalizeOwnerTitle(data.beneficialOwners?.[0]?.title);
    (lead as any).owner1_title = this.normalizeOwnerTitle(data.beneficialOwners?.[0]?.title);

    // PayArc validation: TaxId and MerchantSSN are mutually exclusive (validation.prohibits)
    // For Sole Proprietors: Use MerchantSSN, remove TaxId completely
    // For Businesses: Use TaxId (EIN), do NOT include MerchantSSN
    if (isSoleProp) {
      // Remove TaxId for Sole Proprietors - they use MerchantSSN instead
      delete (lead as any).TaxId;
      if (primarySignerSsn) {
        (lead as any).MerchantSSN = primarySignerSsn;
      }
    }
    // For businesses, TaxId is already set with EIN, and MerchantSSN is not added

    // Only include MerchantOtherTaxId when IdentificationNumberType === '3' (Other)
    // Not used in our current flow; ensure it is not present otherwise

    // Map CRM 'Other.merchantCategory' to PayArc 'MerchantCategory'
    // Values: E (E-Commerce), MOTO (Moto), R (Retail), RT (Restaurant), H (Hotel)
    const rawCategory = (data as any)?.other?.merchantCategory || 'retail';
    const categoryMap: Record<string, string> = {
      ecommerce: 'E',
      'e-commerce': 'E',
      moto: 'MOTO',
      retail: 'R',
      restaurant: 'RT',
      hotel: 'H',
    };
    const normalizedKey = String(rawCategory).trim().toLowerCase();
    const payarcCategory = categoryMap[normalizedKey] || 'R';
    (lead as any).MerchantCategory = payarcCategory;

    return lead;
  }

  private async buildPricingData(data: MerchantApplicationData): Promise<PayArcBlendedPricing> {
    const DEFAULT_PROCESSING_FEE = 3.5;
    const DEFAULT_TRANSACTION_FEE_DOLLARS = 0.3;

    const toNumber = (value: unknown): number | undefined => {
      if (value === null || value === undefined) return undefined;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : undefined;
    };

    // Get CRM values (admin overrides)
    const onlinePricing = data.pricing?.online;
    const offlinePricing = data.pricing?.offline;
    const crmProcessingFee = toNumber(onlinePricing?.processingFee) ?? toNumber(offlinePricing?.processingFee);
    const crmTransactionFee = toNumber(onlinePricing?.transactionFee) ?? toNumber(offlinePricing?.transactionFee);

    // Start with defaults
    let processingFeePct: number = DEFAULT_PROCESSING_FEE;
    let rawTransactionFee: number | undefined;

    // Step 1: Try to get template values as base
    if (data.pricingTemplateId && data.selectedBankingPartnerId) {
      try {
        console.log('Fetching pricing template:', data.pricingTemplateId, 'from partner:', data.selectedBankingPartnerId);

        const { bankingPartnerService } = await import('@/features/banking-partners/services/bankingPartnerService');

        const template = await bankingPartnerService.getPricingTemplate(
          data.selectedBankingPartnerId,
          data.pricingTemplateId
        );

        if (template) {
          console.log('Using pricing template as base:', template.name, template.pricing);
          // Use template values as base
          processingFeePct = template.pricing.processingFees.visaMasterDiscover;
          rawTransactionFee = template.pricing.transactionFees.visaMasterDiscover;
        }
      } catch (error) {
        console.error('Error fetching pricing template:', error);
      }
    }

    // Step 2: Override with CRM values if admin has changed them
    if (crmProcessingFee !== undefined) {
      console.log('CRM override for processing fee:', crmProcessingFee, '(was:', processingFeePct, ')');
      processingFeePct = crmProcessingFee;
    }
    if (crmTransactionFee !== undefined) {
      console.log('CRM override for transaction fee:', crmTransactionFee, '(was:', rawTransactionFee, ')');
      rawTransactionFee = crmTransactionFee;
    }

    // Convert transaction fee to dollars (if in cents)
    let transactionFeeDollars: number | undefined;
    if (rawTransactionFee !== undefined) {
      transactionFeeDollars = rawTransactionFee > 5
        ? Number((rawTransactionFee / 100).toFixed(2))
        : Number(rawTransactionFee.toFixed(2));
    }

    const normalizedTransactionFee = Math.max(
      0,
      transactionFeeDollars ?? DEFAULT_TRANSACTION_FEE_DOLLARS
    );

    console.log('Final BlendedRatePricing values:', { processingFeePct, normalizedTransactionFee });

    return {
      FlatRatePct: processingFeePct,
      Authorizations: normalizedTransactionFee,
      CashDiscountRatePct: 3.0,
      CashDiscountFee: normalizedTransactionFee,
      SurchargeFeeRate: 3.0,
      SurchargeFeeAmount: normalizedTransactionFee,
      BlendedBaseCharge: processingFeePct,
      BlendedPercentageCharge: processingFeePct,
    };
  }

  private buildOwnersData(beneficialOwners: BeneficialOwner[]): PayArcOwner[] {
    return beneficialOwners.map(owner => {
      // Use new homeAddress structure first, fallback to legacy fields
      const homeAddress = owner.homeAddress || {
        street: owner.address || '',
        city: owner.city || '',
        state: owner.state || '',
        zipCode: owner.zipCode || '',
      };
      
      // Clean up state code - remove "US-" prefix if present
      let ownerState = homeAddress.state;
      if (ownerState && ownerState.startsWith('US-')) {
        ownerState = ownerState.substring(3);
      }
      
      return {
        FirstName: owner.firstName,
        LastName: owner.lastName,
        Title: this.normalizeOwnerTitle(owner.title),
        OwnershipPct: parseFloat(owner.ownershipPercentage || '0'),  // PayArc expects number
        SSN: this.formatSSN(owner.ssn),  // PayArc expects exactly 9 digits
        Address: homeAddress.street,
        Country: 'US',
        City: homeAddress.city,
        State: ownerState,
        ZipCode: homeAddress.zipCode,
        PhoneNo: formatPhone(owner.phone || ''),
        BirthDate: formatDate(owner.dateOfBirth),
        DriverLicenseNo: owner.driverLicense || '',
        DriverLicenseState: ownerState,
        Email: owner.email || '',
      };
    });
  }

  private normalizeOwnerTitle(inputTitle?: string): string {
    const allowedTitles = [
      'CEO',
      'Director',
      'Owner',
      'Partner',
      'President',
      'Secretary',
      'Treasurer',
      'Vice President',
    ];

    if (!inputTitle) return 'Owner';
    const normalized = inputTitle.trim();
    // Handle common variants
    const aliasMap: Record<string, string> = {
      'vp': 'Vice President',
      'vice-president': 'Vice President',
      'vice president': 'Vice President',
      'coo': 'Director',
      'cfo': 'Treasurer',
      'cto': 'Director',
      'owner': 'Owner',
      'president': 'President',
      'ceo': 'CEO',
      'secretary': 'Secretary',
      'treasurer': 'Treasurer',
      'partner': 'Partner',
      'director': 'Director',
    };
    const lower = normalized.toLowerCase();
    if (aliasMap[lower]) return aliasMap[lower];
    if (allowedTitles.includes(normalized)) return normalized;
    return 'Owner';
  }

  private formatTaxId(ein: string | undefined): string {
    if (!ein) return '';
    // Remove any existing formatting and ensure exactly 9 digits
    const cleaned = ein.replace(/\D/g, '');
    if (cleaned.length >= 9) {
      // Take only first 9 digits
      return cleaned.slice(0, 9);
    }
    // Pad with zeros if less than 9 digits
    return cleaned.padStart(9, '0');
  }

  private formatSSN(ssn: string | undefined): string {
    if (!ssn) return '';
    // Remove any existing formatting and ensure exactly 9 digits
    const cleaned = ssn.replace(/\D/g, '');
    if (cleaned.length >= 9) {
      // Take only first 9 digits
      return cleaned.slice(0, 9);
    }
    // Pad with zeros if less than 9 digits
    return cleaned.padStart(9, '0');
  }

  private resolveIndustryForPayArc(sicCode: number): {
    processingType: 'Payfac' | 'Traditional';
    industryValue: string;
  } {
    const sicStr = String(sicCode);

    // Check payfac codes first — Payfac uses the numeric MCC code as Industry
    const payfacMatch = PAYFAC_INDUSTRY_CODES.find(c => c.code === sicStr && c.enabled);
    if (payfacMatch) {
      return { processingType: 'Payfac', industryValue: sicStr };
    }

    // Check traditional codes — Traditional uses the text identifier as Industry
    const traditionalMatch = TRADITIONAL_INDUSTRY_CODES.find(c => c.code === sicStr && c.enabled);
    if (traditionalMatch && traditionalMatch.identifier) {
      return { processingType: 'Traditional', industryValue: traditionalMatch.identifier };
    }

    // Fallback: unknown codes default to Payfac with numeric code
    return { processingType: 'Payfac', industryValue: sicStr };
  }

  private parseSicCode(sicCode: string | number | undefined): number {
    console.log('Parsing SIC Code - Input:', sicCode, 'Type:', typeof sicCode);
    
    // Check if SIC code is missing
    if (sicCode === null || sicCode === undefined || sicCode === '') {
      console.log('SIC Code is empty/null/undefined, defaulting to 8099');
      return 8099; // Default to Medical Services if no code provided
    }
    
    // Handle if sicCode is a number already
    if (typeof sicCode === 'number') {
      console.log('SIC Code is already a number:', sicCode);
      return sicCode;
    }
    
    // Convert to string if needed
    const sicStr = String(sicCode).trim();
    
    // Extract just the numeric part if the SIC code contains description
    const match = sicStr.match(/^(\d{4})/);
    if (match) {
      const code = parseInt(match[1], 10);
      console.log('Extracted SIC Code from string:', code);
      return code;
    }
    
    // Try to parse as number
    const parsed = parseInt(sicStr, 10);
    if (isNaN(parsed)) {
      console.log('Could not parse SIC Code string:', sicStr, 'defaulting to 8099');
      return 8099; // Default if we can't parse the string
    }
    
    console.log('Parsed SIC Code:', parsed);
    return parsed;
  }

  private normalizeAddress(businessAddress: any): any {
    if (!businessAddress) {
      return {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      };
    }

    // Clean up state code - remove "US-" prefix if present
    let state = businessAddress.state || '';
    if (state.startsWith('US-')) {
      state = state.substring(3);
    }

    return {
      street: businessAddress.street || businessAddress.fullAddress || '',
      city: businessAddress.city || '',
      state: state,
      zipCode: businessAddress.zipCode || ''
    };
  }

  private mapCorporationType(applicationType: string | undefined, corporationType: string | undefined): string {
    console.log('Mapping Corporation Type - Application Type:', applicationType, 'Corporation Type:', corporationType);
    
    // PayArc accepted values: Government, LLC, Non Profit, Partnership, Private Corp, Public Corp, Sole Prop
    if (applicationType === 'individual') {
      console.log('Individual application, returning Sole Prop');
      return 'Sole Prop';
    }
    
    if (!corporationType) {
      console.log('No corporation type provided, defaulting to LLC');
      return 'LLC';
    }
    // Normalize to handle both codes and human labels
    const raw = String(corporationType).trim();
    const lower = raw.toLowerCase();

    // Map code-like inputs
    if (lower === 'sole_proprietorship' || lower === 'sole proprietorship' || lower === 'sole proprietor' || lower === 'sole prop') return 'Sole Prop';
    if (lower === 'llc' || lower === 'limited liability company' || lower === 'limited liability company (llc)') return 'LLC';
    if (lower === 's_corp' || lower === 's corp' || lower === 's corporation') return 'Private Corp';
    if (lower === 'c_corp' || lower === 'c corp' || lower === 'c corporation') return 'Private Corp';
    if (lower === 'partnership') return 'Partnership';
    if (lower === 'lp' || lower === 'limited partnership' || lower === 'limited partnership (lp)') return 'Partnership';
    if (lower === 'llp' || lower === 'limited liability partnership' || lower === 'limited liability partnership (llp)') return 'Partnership';
    if (lower === 'non_profit' || lower === 'non profit' || lower === 'non-profit organization') return 'Non Profit';
    if (lower === 'government') return 'Government';
    if (lower === 'public_corp' || lower === 'public corporation') return 'Public Corp';

    console.log('Unknown corporation type:', corporationType, 'defaulting to LLC');
    return 'LLC';
  }

  private getIrsFilingType(data: MerchantApplicationData): string {
    // PayArc IRS Filing Types (single letter codes):
    // A: Foreign Entity Verification Pending
    // B: Foreign Entity Identified before 1/1/11
    // C: Non Profit Verified
    // D: Non Profit Verification Pending
    // F: Foreign Entity Verified
    // G: Government Entity
    // J: Financial Institution
    // N: Not Excluded (default for standard businesses)
    
    const corpType = data.corporationInfo?.corporationType;
    
    // Check if it's a non-profit
    if (corpType === 'non_profit') {
      return 'C'; // Non Profit Verified
    }
    
    // Check if it's a government entity
    if (corpType === 'government') {
      return 'G'; // Government Entity
    }
    
    // For all other standard business types, use 'N' (Not Excluded)
    return 'N';
  }

  // Unused but kept for potential future use
  // private mapIndustry(sicCode: string | undefined): string {
  //   // For Payfac validation, Industry must be a valid 4-digit MCC as a string
  //   const code = this.parseSicCode(sicCode ?? 8099);
  //   return String(code);
  // }
}

export const payarcService = PayArcService.getInstance();
